// Hideo - script editor's writing surface: typing in the page. The keys (menus, shortcuts, Enter / Backspace in blocks,
// Tab in lists), the Markdown shortcuts while typing (MD_SHORT and MD_INLINE of src/features/editor/markdown.ts: # heading,
// - list, [] checklist, > quote, --- divider, **bold**, *italic*, `code`, ~~strikethrough~~), automatic links, clicks
// on links, comments and checklist boxes. Ported from the legacy editor/editor.js with the same steps.
// U+200B (zero-width space) is used to step out of just-created bold/code: it is removed on save (noZw).
import { useCommentsStore } from '../../../stores/comments'
import { useDialogsStore } from '../../../stores/dialogs'
import { useTocStore } from '../../../stores/toc'
import { noZw } from '../html'
import { tidyStyles } from '../tidy'
import { MD_INLINE, MD_SHORT } from '../markdown'
import { linkHref, newLink } from '../paste'
import { blockToList, insertLink, listOff } from './commands'
import { edBlockOf, edBody, edCaret, elOf, emptyBlock, ex, queueScriptSave } from './core'
import { hideSelMenu, selMenuShown } from './selmenu'
import { slash, slashCheck, slashClose, slashPick, slashRender } from './slash'
import { blockDupAtCaret, hideBlockHandle } from './blocks'
import { hideLinkBubble, openLinkHref, showLinkBubble } from './links'
import { paste } from './clipboard'

const ZW = '\u200b'

/** Block text from the start up to the cursor (a first line without a paragraph gets one) */
function caretPrefix(): { blk: HTMLElement; r: Range; text: string } | null {
  const s = getSelection() as Selection
  if (!s.rangeCount || !s.isCollapsed) return null
  if (s.anchorNode && s.anchorNode.parentNode === edBody() && s.anchorNode.nodeType === 3) document.execCommand('formatBlock', false, '<p>')   // first line without a paragraph
  const blk = edBlockOf(s.anchorNode)
  if (!blk) return null
  const r = document.createRange(); r.setStart(blk, 0); r.setEnd(s.anchorNode as Node, s.anchorOffset)
  return { blk, r, text: noZw(r.toString()) }
}
/** Space after "#", "-", "1.", "[]", ">", "```"… at the start of a paragraph: the block */
function mdShortcut(e: KeyboardEvent): boolean {
  const c = caretPrefix()
  if (!c || !/^(P|DIV)$/.test(c.blk.tagName) || c.blk.className) return false
  const v = MD_SHORT[c.text]
  if (!v) return false
  e.preventDefault(); c.r.deleteContents()
  if (!c.blk.textContent && !c.blk.querySelector('br')) c.blk.appendChild(document.createElement('br'))
  edCaret(c.blk, 0)
  if (v === 'todo' || v === 'ul' || v === 'ol') edCaret(blockToList(c.blk, v), 1)   // by hand: insertUnorderedList sometimes put it INSIDE the paragraph
  else {
    document.execCommand('formatBlock', false, '<' + v + '>')
    if (c.blk.isConnected && !c.blk.textContent && !c.blk.children.length) c.blk.remove()   // Chrome made the new block beside the paragraph, left empty
  }
  queueScriptSave(); useTocStore().queue(); return true
}
/** Enter on an empty item (checklists too) ends the list: in a list inside a list the item goes up one level, otherwise
 *  it becomes a paragraph after the list; the items after it stay a list. Done by hand, the same in every browser. */
function exitEmptyLi(e: KeyboardEvent): boolean {
  const s = getSelection() as Selection, n = s.anchorNode, li = n && (elOf(n) as Element).closest('li')
  if (!li || !edBody().contains(li) || noZw(li.textContent).trim() || li.querySelector('ul,ol')) return false
  const list = li.parentElement as HTMLElement, up = list.parentElement as HTMLElement
  e.preventDefault()
  const after = [...list.children].slice([...list.children].indexOf(li) + 1)
  const rest = after.length ? list.cloneNode(false) as HTMLElement : null
  if (rest) rest.append(...after)
  let to: HTMLElement
  if (/^(UL|OL|LI)$/.test(up.tagName)) {   // list inside a list (<ul> in the list, or in an item): one level up
    to = emptyBlock('li')
    if (up.tagName === 'LI') { up.after(to); if (rest) to.append(rest) } else list.after(to, ...(rest ? [rest] : []))
  } else {   // a list inside a paragraph (made by the "/" menu before) ends after the paragraph
    to = emptyBlock()
    ;(up.tagName === 'P' ? up : list).after(to, ...(rest ? [rest] : []))
  }
  li.remove(); if (!list.children.length) list.remove()
  if (up.tagName === 'P' && !up.childNodes.length) up.remove()
  edCaret(to, 0); queueScriptSave(); return true
}
/** Enter in a quote: at the end it moves to a normal paragraph (Shift+Enter = new line in the quote).
 *  Enter in code: new line inside the code; on an empty last line it exits the code. */
function blockEnter(e: KeyboardEvent): boolean {
  const c = caretPrefix()
  if (!c) return false
  const b = c.blk, all = noZw(b.textContent), atEnd = c.text.length >= all.length, newP = () => emptyBlock()
  const end = c.r.endContainer, inLi = (elOf(end) as Element).closest('li')
  if (b.tagName === 'BLOCKQUOTE' && (atEnd || !all.trim()) && !inLi) {   // (an older list inside a quote: the list's Enter)
    e.preventDefault()
    const p = newP()
    if (!all.trim()) b.replaceWith(p); else b.after(p)
    edCaret(p, 0); queueScriptSave(); return true
  }
  if (b.tagName === 'PRE') {
    e.preventDefault()
    if (atEnd && /\n\u200b?$/.test(b.textContent as string)) { b.textContent = (b.textContent as string).replace(/\n\u200b?$/, ''); const p = newP(); b.after(p); edCaret(p, 0) }
    else {   // by hand: insertText would turn the newline into <br>, which the code (textContent) would lose
      const r = (getSelection() as Selection).getRangeAt(0), nl = document.createTextNode(atEnd ? '\n' + ZW : '\n')
      r.deleteContents(); r.insertNode(nl); edCaret(nl, nl.length)
    }
    queueScriptSave(); return true
  }
  return false
}
/** Backspace at the start of a list's first item: the item becomes a paragraph (the browser joined its text to the
 *  block above, a quote or a heading, with that block's look) */
function listBackspace(e: KeyboardEvent): boolean {
  const s = getSelection() as Selection, n = s.anchorNode
  if (!s.isCollapsed || !n) return false
  const li = (elOf(n) as Element).closest('li'), list = li && li.parentElement
  if (!li || !list || list.parentElement !== edBody() || list.firstElementChild !== li) return false
  const r = document.createRange(); r.setStart(li, 0); r.setEnd(n, s.anchorOffset)
  if (noZw(r.toString()) || r.cloneContents().querySelector('img,.mention-chip')) return false
  e.preventDefault(); listOff(list as HTMLElement); return true
}
/** Backspace at the start of a heading, quote or code: back to normal text */
function blockBackspace(e: KeyboardEvent): boolean {
  const c = caretPrefix()
  if (!c || c.text !== '' || !/^(H[1-4]|BLOCKQUOTE|PRE)$/.test(c.blk.tagName)) return false
  e.preventDefault(); document.execCommand('formatBlock', false, '<p>'); queueScriptSave(); return true
}
/** "---" becomes a divider */
function mdDivider(e: KeyboardEvent): boolean {
  const c = caretPrefix()
  if (!c || c.blk.tagName !== 'P' || c.text !== '--' || noZw(c.blk.textContent) !== '--') return false
  e.preventDefault()
  const hr = document.createElement('hr'), p = emptyBlock()
  c.blk.replaceWith(hr, p); edCaret(p, 0); queueScriptSave(); return true
}
/** Inline Markdown just closed before the cursor (**bold**, *italic*, _italic_, `code`, ~~strikethrough~~) */
function mdInline(): void {
  const s = getSelection() as Selection
  if (!s.rangeCount || !s.isCollapsed) return
  const n = s.anchorNode, o = s.anchorOffset
  if (!n || n.nodeType !== 3 || !n.parentElement || n.parentElement.closest('code,pre,a')) return
  const before = (n.textContent as string).slice(0, o)
  for (const [re, tag, mk] of MD_INLINE) {
    const m = before.match(re)
    if (!m) continue
    const start = o - m[1].length - 2 * mk, r = document.createRange(); r.setStart(n, start); r.setEnd(n, o); r.deleteContents()
    const el = document.createElement(tag); el.textContent = m[1]
    const after = document.createTextNode(ZW); r.insertNode(after); r.insertNode(el)
    edCaret(after, 1); queueScriptSave(); return
  }
}
/** A typed address becomes a link on space or Enter */
function autoLinkBeforeCaret(e: KeyboardEvent): void {
  const s = getSelection() as Selection
  if (!s.rangeCount || !s.isCollapsed) return
  const n = s.anchorNode, o = s.anchorOffset
  if (!n || n.nodeType !== 3 || !n.parentElement || n.parentElement.closest('a,code,pre')) return
  const m = (n.textContent as string).slice(0, o).match(/(?:^|\s)((?:https?:\/\/|www\.)[^\s<>"']+)$/i)
  if (!m) return
  const url = m[1].replace(/[.,;:!?)\]}]+$/, ''), h = linkHref(url)
  if (!h || url.length < 5) return
  const tail = m[1].length - url.length, end = o - tail, start = end - url.length
  const r = document.createRange(); r.setStart(n, start); r.setEnd(n, end)
  const a = newLink(h); r.surroundContents(a)
  let rest = a.nextSibling as Text | null
  if (!rest || rest.nodeType !== 3) { rest = document.createTextNode(''); a.after(rest) }
  if (e.key === ' ') {
    e.preventDefault(); rest.insertData(tail, tail >= rest.length ? '\u00a0' : ' '); edCaret(rest, tail + 1)
  } else edCaret(rest, tail)
  queueScriptSave()
}

function onKeydown(e: KeyboardEvent): void {
  // While the teleprompter is on top its own Esc (src/features/keyboard.ts) closes it first: the page's menus wait
  if (e.key === 'Escape' && useDialogsStore().isOpen('teleprompter')) return
  if (selMenuShown() && e.key === 'Escape') { e.stopPropagation(); hideSelMenu(); return }
  if (slash.open) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); slash.idx = (slash.idx + (e.key === 'ArrowDown' ? 1 : -1) + slash.items.length) % slash.items.length; slashRender(); return }
    if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); slashPick(slash.idx); return }
    if (e.key === 'Escape') { e.stopPropagation(); slashClose(); return }
  }
  if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); void insertLink(); return }
  if ((e.ctrlKey || e.metaKey) && e.altKey && e.key.toLowerCase() === 'm') { e.preventDefault(); useCommentsStore().start(); return }
  if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'd') { e.preventDefault(); blockDupAtCaret(); return }
  if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'v') { paste.plain = true; setTimeout(() => { paste.plain = false }, 400); return }
  hideBlockHandle(); hideLinkBubble()
  if (e.key === 'Tab' && !e.ctrlKey && !e.altKey) {   // in lists Tab = indent, Shift+Tab = outdent; elsewhere nothing (the cursor stays in the page)
    const s = getSelection() as Selection, n = s.anchorNode, li = n && (elOf(n) as Element).closest('li')
    e.preventDefault()
    if (li && edBody().contains(li)) ex(e.shiftKey ? 'outdent' : 'indent')
    return
  }
  if (e.ctrlKey || e.metaKey || e.altKey) return
  if (e.key === 'Enter' && !e.shiftKey && (exitEmptyLi(e) || blockEnter(e))) return
  if (e.key === 'Backspace' && (listBackspace(e) || blockBackspace(e))) return
  if (e.key === ' ' && mdShortcut(e)) return
  if (e.key === '-' && mdDivider(e)) return
  if (e.key === ' ' || e.key === 'Enter') autoLinkBeforeCaret(e)
}
function onClick(e: MouseEvent): void {
  slashClose()
  const tg = e.target as Element, a = tg.closest('a'), cm = tg.closest<HTMLElement>('.cmt')
  if (cm && (getSelection() as Selection).isCollapsed) useCommentsStore().focus(cm.dataset.cmt as string)   // commented text: show its comment
  if (!a || !edBody().contains(a)) return hideLinkBubble()
  if (e.ctrlKey || e.metaKey) { e.preventDefault(); openLinkHref(a); return }
  showLinkBubble(a)
}
/** Click on the box (drawn left of the item) = tick / untick */
function onMousedown(e: MouseEvent): void {
  const tg = e.target as Element, li = tg.closest && tg.closest('ul.todo > li')
  if (!li) return
  if (e.clientX >= li.getBoundingClientRect().left) return   // click on the text, not on the box
  e.preventDefault(); li.toggleAttribute('data-done'); if (li.hasAttribute('data-done')) li.setAttribute('data-done', '1'); queueScriptSave()
}
function onInput(ev: Event): void {
  const e = ev as InputEvent
  if (e.inputType === 'insertText' && /^[*`~_]$/.test(e.data || '')) mdInline()
  if (e.inputType === 'insertParagraph') {   // new checklist item: starts unticked (the browser copies the attributes)
    const s = getSelection() as Selection, n = s.anchorNode, li = n && (elOf(n) as Element).closest('ul.todo > li')
    if (li) li.removeAttribute('data-done')
  }
  // the styles the browser wrote by itself when joining or moving text (tidy.ts): the block with the cursor and its
  // neighbours, where the joined text ends up. While typing, only around a styled span with the cursor (where Chrome
  // puts the look the text had before a join): the rest of the block can't have changed, and tidying reads the
  // computed styles of every styled span (the user's colours too) at every key
  const n = (getSelection() as Selection).anchorNode
  if (/^insert(Text|CompositionText|ReplacementText)$/.test(e.inputType)) {
    const sp = n && elOf(n)?.closest('span[style]')
    if (sp && sp.parentElement && edBody().contains(sp)) tidyStyles(sp.parentElement)
    return
  }
  const b = edBlockOf(n)
  if (b) [b.previousElementSibling, b, b.nextElementSibling].forEach(x => tidyStyles(x))
}
/** The page's keydown, click and mousedown listeners (in the legacy order) */
export function installKeys(body: HTMLElement): void {
  body.addEventListener('keydown', onKeydown)
  body.addEventListener('click', onClick)
  body.addEventListener('mousedown', onMousedown)
}
/** The page's input listeners (after paste, copy and cut, like before): first what the page's inline oninput did (the
 *  text is saved 600 ms later, the "/" menu opens or filters), then the Markdown shortcuts and the rest */
export function installInput(body: HTMLElement): void {
  body.addEventListener('input', () => { queueScriptSave(); slashCheck() })
  body.addEventListener('input', onInput)
}
